import React from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import { useCategories } from "@/hooks/useCategories";
import ListingGrid from "@/components/listings/ListingGrid";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import CategoryIcon from "@/components/common/CategoryIcon";
import CategoryBanner from "@/components/common/CategoryBanner";
import FollowCategoryButton from "@/components/common/FollowCategoryButton";
import JobAlertButton from "@/components/jobs/JobAlertButton";
import { CategoryCircleGrid } from "@/components/home/CircularTileCarousel";
import CarHistoryCheckCard from "@/components/category/CarHistoryCheckCard";
import SchemaFilterRail from "@/components/search/SchemaFilterRail";
import NotFoundPage from "@/pages/NotFoundPage";
import Pagination from "@/components/listings/Pagination";
import SortDropdown from "@/components/listings/SortDropdown";
import NearestChip from "@/components/listings/NearestChip";
import { Link } from "react-router-dom";

const PAGE_SIZE = 24;

// Stable empty params for the rail — the category page itself carries no
// filter state; the first applied filter navigates into /search.
const EMPTY_PARAMS = new URLSearchParams();

export default function CategoryPage() {
  const { categorySlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const railParams = EMPTY_PARAMS;
  const region = searchParams.get("region") || undefined;
  // Iter-396 — page rides in the URL (shareable, back-button friendly).
  const page = Math.max(1, parseInt(searchParams.get("page"), 10) || 1);
  // Iter-432 — sort rides in the URL too ("" = Featured first default).
  const sort = searchParams.get("sort") || "";
  // Iter-600 §1 — nearest-first: coords ride in the URL alongside sort.
  const lat = searchParams.get("lat") || "";
  const lng = searchParams.get("lng") || "";
  const nearestActive = sort === "distance" && !!lat && !!lng;
  const { data: categories = [] } = useCategories();

  // WO 800 §2 — authoritative metadata (title/h1/description/canonical/
  // robots/json_ld) comes from the shared backend's SEO endpoint; the
  // client no longer guesses. 404 here = unknown category.
  const seoQ = useQuery({
    queryKey: ["seo-category", categorySlug],
    queryFn: async () => {
      const { data } = await sharedApi.get(`/classifieds/seo/category/${categorySlug}`);
      return data;
    },
    enabled: !!categorySlug,
    retry: false,
    staleTime: 10 * 60 * 1000,
  });

  const category = categories.find((c) => c.slug === categorySlug);
  const flatMatch = categories.flatMap((c) => c.children || []).find((c) => c.slug === categorySlug);

  const q = useQuery({
    queryKey: ["category-listings", categorySlug, page, sort, lat, lng],
    queryFn: async () => {
      const params = { country: "NZ", category: categorySlug, limit: PAGE_SIZE, page };
      if (sort) params.sort = sort;
      if (nearestActive) {
        params.lat = lat;
        params.lng = lng;
      }
      const { data } = await sharedApi.get("/classifieds/listings", { params });
      return data;
    },
    enabled: !!categorySlug,
    // v5 — keep the previous grid on screen while the next page loads.
    placeholderData: (prev) => prev,
  });

  // Iter-432 — sort change resets to page 1 (and drops nearest coords).
  const applySort = (v) => {
    const next = new URLSearchParams(searchParams);
    next.delete("page");
    next.delete("lat");
    next.delete("lng");
    if (v && v !== "distance") next.set("sort", v);
    else next.delete("sort");
    setSearchParams(next, { replace: false });
  };

  // Iter-600 §1 — nearest chip handlers.
  const applyNearest = ({ lat: la, lng: lo }) => {
    const next = new URLSearchParams(searchParams);
    next.delete("page");
    next.set("sort", "distance");
    next.set("lat", la);
    next.set("lng", lo);
    setSearchParams(next, { replace: false });
  };
  const clearNearest = () => applySort("");

  // Iter-396 — page change updates the URL and scrolls to top.
  const goToPage = (n) => {
    const next = new URLSearchParams(searchParams);
    if (n <= 1) next.delete("page");
    else next.set("page", String(n));
    setSearchParams(next, { replace: false });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (categories.length > 0 && !category && !flatMatch) {
    // WO 800 §2/§4 — unknown /c/{slug} renders a genuine 404 in place
    // (was a soft redirect into /search). While the SEO endpoint is still
    // resolving, hold the frame to avoid a 404 flash on slow networks.
    if (seoQ.isError) return <NotFoundPage />;
    if (!seoQ.data) return null;
  }

  const seo = seoQ.data;
  const info = category || flatMatch || { name: seo?.h1 || categorySlug, icon: null, children: [] };

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const canonical = seo?.canonical || `${origin}/c/${categorySlug}`;
  const listingsForLd = (q.data?.listings || []).slice(0, 20);
  const fallbackLd = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: origin || "/" },
        { "@type": "ListItem", position: 2, name: info.name, item: canonical },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `${info.name} in New Zealand`,
      url: canonical,
      isPartOf: { "@type": "WebSite", name: "Allsale Classified", url: origin || "/" },
      mainEntity: {
        "@type": "ItemList",
        numberOfItems: q.data?.total ?? listingsForLd.length,
        itemListElement: listingsForLd.map((l, i) => ({
          "@type": "ListItem",
          position: i + 1,
          url: `${origin}/${l.category_slug}/${(l.title || "ad").toLowerCase().replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-").slice(0, 80)}-${l.id}`,
          name: l.title,
        })),
      },
    },
  ];
  const jsonLd = seo?.json_ld?.length ? seo.json_ld : fallbackLd;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <Seo
        title={seo?.title || `${info.name} in New Zealand`}
        description={seo?.description || `Browse ${info.name.toLowerCase()} classifieds across New Zealand — free to post, easy to browse.`}
        url={canonical}
        jsonLd={jsonLd}
        robots={seo?.robots && seo.robots !== "index,follow" ? seo.robots : undefined}
      />
      <Breadcrumbs items={[{ label: info.name }]} />

      <div className="mt-4">
        <CategoryBanner categorySlug={categorySlug} />
      </div>

      <div className="mt-6 flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] grid place-items-center">
          <CategoryIcon name={info.icon} thumbnailUrl={info.thumbnail_url} emoji={info.emoji} alt={info.name} className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900">{seo?.h1 || info.name}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <p className="text-sm text-slate-500" data-testid="category-results-count">
              {q.data?.total ?? 0} {info.name.toLowerCase()} across Aotearoa
            </p>
            {/* Iter-432 — right-aligned sort dropdown (all categories) */}
            <NearestChip active={nearestActive} onCoords={applyNearest} onClear={clearNearest} testId="category-nearest-chip" />
            <SortDropdown value={sort} onChange={applySort} categorySlug={categorySlug} />
          </div>
        </div>
        <FollowCategoryButton
          categorySlug={categorySlug}
          categoryName={info.name}
          region={region}
        />
        {categorySlug === "jobs" && (
          <JobAlertButton presetRegion={region || ""} />
        )}
      </div>

      {info.children?.length > 0 && (
        <div className="mt-8">
          <CategoryCircleGrid
            items={info.children}
            hrefBuilder={(sub) => `/search?category=${sub.slug}`}
            ariaLabel={`${info.name} subcategories`}
            testId="category-subcat-grid"
          />
        </div>
      )}

      {categorySlug === "cars" && (
        <div className="mt-8">
          <CarHistoryCheckCard />
        </div>
      )}

      {/* Founder ask (2026-02): the refine rail opens right on the category
          page. Any filter tap carries the visitor into the filtered results
          on /search with this category preselected. */}
      <div className="mt-10 flex flex-col lg:flex-row gap-8">
        <SchemaFilterRail
          params={railParams}
          categorySlug={categorySlug}
          onApply={(patch) => {
            const next = new URLSearchParams({ category: categorySlug });
            Object.entries(patch).forEach(([k, v]) => {
              if (v !== "" && v != null) next.set(k, v);
            });
            navigate(`/search?${next.toString()}`);
          }}
        />
        <div className="flex-1 min-w-0">
          <ListingGrid loading={q.isLoading} listings={q.data?.listings || []} empty={`No ${info.name} listings yet.`} />
          <Pagination page={page} total={q.data?.total ?? 0} limit={PAGE_SIZE} onPage={goToPage} />
          {categorySlug === "jobs" && !q.isLoading && (q.data?.listings || []).length === 0 && (
            <div className="mt-4 flex justify-center" data-testid="job-alert-empty-cta">
              <JobAlertButton presetRegion={region || ""} testId="job-alert-cta-empty" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
